import { showToast } from 'common/utils'
import {
  MutationCache,
  QueryCache,
  QueryClient,
  type QueryMeta
} from 'react-query'
import { GENERIC_API_ERROR_MESSAGE, isAbortError, isApiError } from './apiError'
import { getHomeChangedModalOpen } from './homeSwitchBroadcast'

/**
 * Opts a query or mutation into the global error toast:
 * `useQuery(key, queryFn, { meta: errorToastMeta })`.
 *
 * Without it a failed request is silent — react-query parks the error on the
 * hook and nobody tells the user (CM-2340).
 */
export const errorToastMeta = { showErrorToast: true } as const

/**
 * Toasts a failed request, unless it is one of the failures a user can do
 * nothing with:
 *
 * - aborted requests — `lmcRequest` shares one AbortController, so any request
 *   started with `newSignal` cancels the ones still in flight
 * - anything failing while the home-changed modal is up: it already explains
 *   why every in-flight request has just died
 * - a 404 answering a read, via `quietWhenMissing`. CM-2340 asks for those to
 *   be tracked backend-side rather than raised here, and reads kept their
 *   pre-CM-2340 silence until someone audits which of them can 404 at all.
 *   Writes pass no flag: a 404 there means the record being changed is gone,
 *   which the user does need telling.
 */
export const handleGlobalError = (
  error: unknown,
  meta: QueryMeta | undefined,
  { quietWhenMissing = false }: { quietWhenMissing?: boolean } = {}
) => {
  if (!meta?.showErrorToast) return
  if (isAbortError(error)) return
  if (quietWhenMissing && isApiError(error) && error.status === 404) return
  if (getHomeChangedModalOpen()) return

  showToast({
    type: 'error',
    // A non-ApiError never reached the backend (offline, unparseable body, a
    // bug in the query function), so its message isn't fit to show a user.
    title: isApiError(error) ? error.message : GENERIC_API_ERROR_MESSAGE
  })
}

export const createQueryClient = () =>
  new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) =>
        handleGlobalError(error, query.meta, { quietWhenMissing: true })
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) =>
        handleGlobalError(error, mutation.options.meta)
    }),
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000
      }
    }
  })
